import { useState, useRef, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useAnalytics } from '@libs/mixpanel/useAnalytics';

export const useVisitDetailPhotoCapture = (
  onClose: () => void,
  onCapture: (imageData: string, description?: string) => Promise<void>,
) => {
  const intl = useIntl();
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const { trackEvent } = useAnalytics();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const compressImage = useCallback(
    async (imageData: string, maxWidth: number = 1200, quality: number = 0.7): Promise<string> => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }

          ctx.drawImage(img, 0, 0, width, height);
          const compressedData = canvas.toDataURL('image/jpeg', quality);
          resolve(compressedData);
        };
        img.onerror = reject;
        img.src = imageData;
      });
    },
    [],
  );

  const stopCamera = useCallback(() => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
  }, [cameraStream]);

  const startCamera = useCallback(async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
      });
      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error('Error accessing camera:', err);
      setError(
        intl.formatMessage({
          id: 'hairstyle.photo.camera.error',
          defaultMessage: 'Nepodařilo se získat přístup ke kameře. Zkuste použít výběr souboru.',
        }),
      );
    }
  }, [intl]);

  const capturePhoto = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current) {
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    ctx.drawImage(video, 0, 0);
    const imageData = canvas.toDataURL('image/jpeg', 0.95);

    const compressedImage = await compressImage(imageData);
    setCapturedImage(compressedImage);
    stopCamera();
  }, [compressImage, stopCamera]);

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }

      const reader = new FileReader();
      reader.onload = async e => {
        const imageData = e.target?.result as string;
        const compressedImage = await compressImage(imageData);
        setCapturedImage(compressedImage);
      };
      reader.readAsDataURL(file);
    },
    [compressImage],
  );

  const handleClose = useCallback(() => {
    stopCamera();
    setCapturedImage(null);
    setDescription('');
    setError(null);
    onClose();
  }, [stopCamera, onClose]);

  const handleUpload = useCallback(async () => {
    if (!capturedImage) {
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      await onCapture(capturedImage, description);
      trackEvent('visit_photo_uploaded', { has_description: Boolean(description.trim()) });
      handleClose();
    } catch (err) {
      console.error('Error uploading photo:', err);
      setError(
        intl.formatMessage({
          id: 'hairstyle.photo.upload.error',
          defaultMessage: 'Nepodařilo se nahrát fotografii. Zkuste to prosím znovu.',
        }),
      );
    } finally {
      setIsUploading(false);
    }
  }, [capturedImage, description, onCapture, handleClose, intl, trackEvent]);

  const handleRetake = useCallback(() => {
    setCapturedImage(null);
    startCamera();
  }, [startCamera]);

  return {
    capturedImage,
    description,
    setDescription,
    isUploading,
    error,
    cameraStream,
    videoRef,
    canvasRef,
    fileInputRef,
    startCamera,
    capturePhoto,
    handleFileSelect,
    handleUpload,
    handleClose,
    handleRetake,
  };
};
